/**
 * Utility functions for input validation
 * These can be used across the entire application
 */

/**
 * Prevents space key from being typed at the start (leading space) or if field is empty
 * @param event - KeyboardEvent from input field
 */
export function preventLeadingSpaceKey(event: KeyboardEvent): void {
  if (event.key === " " || event.code === "Space") {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value || "";
    const cursorPosition = target.selectionStart || 0;

    // Prevent space if:
    // 1. Field is empty
    // 2. Cursor is at the start (position 0)
    const isEmpty = currentValue.length === 0;
    const isAtStart = cursorPosition === 0;

    if (isEmpty || isAtStart) {
      event.preventDefault();
      return;
    }
  }
}

/**
 * Handles paste event and removes leading spaces from pasted text
 * Keeps center and trailing spaces intact
 * @param event - ClipboardEvent from input field
 * @param onValueUpdate - Callback function to update the model value
 */
export function handlePasteWithoutLeadingSpaces(
  event: ClipboardEvent,
  onValueUpdate?: (newValue: string) => void
): void {
  event.preventDefault();
  const clipboardData = event.clipboardData || (window as any).clipboardData;
  const pastedText = clipboardData.getData("text");

  // Remove only leading spaces from pasted text, keep center and trailing spaces
  const textWithoutLeadingSpaces = pastedText.replace(/^\s+/, "");

  // Insert the text without leading spaces
  const target = event.target as HTMLInputElement;
  const start = target.selectionStart || 0;
  const end = target.selectionEnd || 0;
  const currentValue = target.value || "";

  // Insert text at cursor position
  const newValue =
    currentValue.substring(0, start) +
    textWithoutLeadingSpaces +
    currentValue.substring(end);

  // Remove only leading spaces from the final value (keep center and trailing spaces)
  const finalValue = newValue.replace(/^\s+/, "");

  // Update the value using callback if provided, otherwise update directly
  if (onValueUpdate) {
    onValueUpdate(finalValue);
  } else {
    (target as any).value = finalValue;
    // Trigger input event for ngModel binding
    target.dispatchEvent(new Event("input", { bubbles: true }));
  }

  // Set cursor position after inserted text
  setTimeout(() => {
    const newCursorPos = start + textWithoutLeadingSpaces.length;
    target.setSelectionRange(newCursorPos, newCursorPos);
  }, 0);
}

/**
 * Removes leading spaces from a value when blur event occurs
 * @param value - Current input value
 * @returns Value without leading spaces
 */
export function removeLeadingSpaces(value: string | null | undefined): string {
  if (!value) return "";
  return value.replace(/^\s+/, "");
}

/**
 * Trims all string properties on a plain object.
 * Useful before building search / submit payloads so that
 * leading / trailing spaces are not sent to the backend.
 */
export function trimStringFields<T extends Record<string, any>>(obj: T): T {
  if (!obj || typeof obj !== "object") {
    return obj;
  }

  Object.keys(obj).forEach((key) => {
    const value = (obj as any)[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      (obj as any)[key] = trimmed === "" ? "" : trimmed;
    }
  });

  return obj;
}


